/** Composition provenance accordion — layout / theme / blocks / language of a
 *  test-rendered channel. Extracted from TestRenderPage for reuse in the
 *  notification editor's TestRenderDialog.
 */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { CompositionRecord } from '@/api/notificationTestRender';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { blockActions, languagePolicyActions, themeActions } from '../notificationLinks';

export function ProvenancePanel({ composition }: { composition: CompositionRecord }) {
  const { layout, theme, blocks, language } = composition;
  const layoutLabel = layout.custom
    ? 'Custom template (delivery method body)'
    : `Standard template #${layout.id} “${layout.name ?? ''}” v${layout.version ?? '?'} (${layout.template_language ?? '?'})`;

  return (
    <Accordion variant="outlined" disableGutters sx={{ mt: 1.5, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Composition provenance
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Layout
            </Typography>
            <Typography variant="body2">{layoutLabel}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Theme
            </Typography>
            {theme ? (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2">
                  <ValueActions actions={themeActions(theme)}>
                    #{theme.id} {theme.codename}
                  </ValueActions>
                  {theme.tour_operator_id == null ? ' (system)' : ''}
                </Typography>
                {theme.shell_wrapped != null && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={theme.shell_wrapped ? 'success' : 'default'}
                    label={theme.shell_wrapped ? 'shell wrapped' : 'shell not applied'}
                  />
                )}
                {theme.signature_appended != null && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={theme.signature_appended ? 'success' : 'default'}
                    label={theme.signature_appended ? 'signature appended' : 'no signature'}
                  />
                )}
                {theme.inline_css && <Chip size="small" variant="outlined" label="inline CSS" />}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No theme in effect
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Blocks
            </Typography>
            {blocks.length ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Codename</TableCell>
                    <TableCell>Alias</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell>Override</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blocks.map((b) => (
                    <TableRow key={`${b.id}-${b.alias}`}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        <ValueActions actions={blockActions(b)}>{b.codename}</ValueActions>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {b.alias === b.codename ? '—' : b.alias}
                      </TableCell>
                      <TableCell>{b.type}</TableCell>
                      <TableCell>{b.format}</TableCell>
                      <TableCell>{b.channel_override_used ? 'channel override' : '—'}</TableCell>
                      <TableCell>{b.source}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No blocks attached
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Language
            </Typography>
            <Typography variant="body2">
              {language.resolved ?? '—'}{' '}
              {language.policy_id != null ? (
                <ValueActions actions={languagePolicyActions({ id: language.policy_id })}>
                  (language policy #{language.policy_id})
                </ValueActions>
              ) : (
                '(default resolution, no policy)'
              )}
            </Typography>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
